/**
 * Client API for the orchestrator's LiveKit endpoints.
 *
 * The SFU call path is opt-in by server capability: if the orchestrator
 * reports `configured: true`, calls go through LiveKit; otherwise the legacy
 * mesh path is used. The client never holds the LiveKit API secret — it asks
 * the orchestrator for a short-lived room token, authorized with the same
 * credential it already uses for chat (room-key proof or guest session JWT).
 */
/**
 * Typed error category sentinels stored in callError state so the UI can
 * show a specific, actionable message instead of the generic fallback.
 *
 * TOKEN_HTTP_ERROR   – orchestrator returned a non-2xx status for /livekit/token
 *                      (server is reachable; credentials or permissions issue)
 * TOKEN_NETWORK_ERROR – fetch to /livekit/token failed with a network error
 *                      (DNS failure, connection refused)
 * TOKEN_TIMEOUT_ERROR – /livekit/token fetch timed out
 * CONNECT_TIMEOUT_ERROR – room.connect() timed out (likely firewall / UDP blocked)
 * CONNECT_FAILED_ERROR  – room.connect() threw a network / WebSocket error
 */
export const TOKEN_HTTP_ERROR = 'sfu-token-http-error';
export const TOKEN_NETWORK_ERROR = 'sfu-token-network-error';
export const TOKEN_TIMEOUT_ERROR = 'sfu-token-timeout';
export const CONNECT_TIMEOUT_ERROR = 'sfu-connect-timeout';
export const CONNECT_FAILED_ERROR = 'sfu-connect-failed';
/** Attach a category sentinel to an Error so callers can read it without parsing. */
export function categorizedError(msg, category) {
    const err = new Error(msg);
    err.sfuCategory = category;
    return err;
}
/** Read the category back from a categorized error (undefined for uncategorized). */
export function getSfuCategory(err) {
    if (err instanceof Error)
        return err.sfuCategory;
    return undefined;
}
/** How long to wait for the token endpoint before giving up. */
const TOKEN_FETCH_TIMEOUT_MS = 10_000;
function orchestratorHttpBase() {
    const base = process.env.CHAT_WS_URL || process.env.ORCHESTRATOR_URL || '/orchestrator';
    return base.replace(/\/+$/, '');
}
/** Is the SFU available? Fast + fail-closed: any error means "not available". */
export async function isLiveKitConfigured() {
    try {
        const ctrl = new AbortController();
        const timer = setTimeout(() => ctrl.abort(), 4000);
        const res = await fetch(`${orchestratorHttpBase()}/livekit/config`, { signal: ctrl.signal });
        clearTimeout(timer);
        if (!res.ok)
            return false;
        const data = await res.json();
        return !!data?.configured;
    }
    catch {
        return false;
    }
}
export function buildLiveKitWalletAuthMessage(opts) {
    return [
        'Dwinity LiveKit room authorization',
        `Room: ${opts.room.toLowerCase()}`,
        `Device: ${opts.deviceId.slice(0, 64)}`,
        `Timestamp: ${opts.timestamp}`,
    ].join('\n');
}
/**
 * Fetch a LiveKit room token. Throws with a categorized Error on failure —
 * callers surface this to the user (no silent fallback mid-call).
 *
 * Error categories (readable via getSfuCategory):
 *   TOKEN_HTTP_ERROR   – server replied with a non-2xx status
 *   TOKEN_NETWORK_ERROR – fetch failed (DNS / connection refused)
 *   TOKEN_TIMEOUT_ERROR – request timed out after TOKEN_FETCH_TIMEOUT_MS
 */
export async function getLiveKitToken(opts) {
    const headers = { 'Content-Type': 'application/json' };
    let walletAuth;
    if (!opts.guestToken && !opts.deviceSessionToken) {
        if (!opts.signWalletMessage) {
            throw categorizedError('wallet proof is unavailable', TOKEN_HTTP_ERROR);
        }
        const timestamp = Date.now();
        try {
            const signature = await opts.signWalletMessage(buildLiveKitWalletAuthMessage({
                room: opts.room,
                deviceId: opts.deviceId,
                timestamp,
            }));
            if (!signature)
                throw new Error('empty wallet signature');
            walletAuth = { timestamp, signature };
        }
        catch {
            throw categorizedError('wallet proof was not signed', TOKEN_HTTP_ERROR);
        }
    }
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), TOKEN_FETCH_TIMEOUT_MS);
    let res;
    try {
        res = await fetch(`${orchestratorHttpBase()}/livekit/token`, {
            method: 'POST',
            headers,
            body: JSON.stringify({
                room: opts.room.toLowerCase(),
                deviceId: opts.deviceId,
                guest_token: opts.guestToken || undefined,
                device_session_token: opts.deviceSessionToken || undefined,
                wallet_auth: walletAuth,
            }),
            signal: ctrl.signal,
        });
    }
    catch (fetchErr) {
        clearTimeout(timer);
        const name = fetchErr instanceof Error ? fetchErr.name : '';
        if (name === 'AbortError') {
            throw categorizedError('livekit token request timed out', TOKEN_TIMEOUT_ERROR);
        }
        throw categorizedError(fetchErr instanceof Error ? fetchErr.message : 'livekit token fetch failed', TOKEN_NETWORK_ERROR);
    }
    clearTimeout(timer);
    if (!res.ok) {
        let detail = `${res.status}`;
        try {
            detail = (await res.json())?.error || detail;
        }
        catch { /* keep default */ }
        throw categorizedError(`livekit token request failed (${detail})`, TOKEN_HTTP_ERROR);
    }
    const data = await res.json();
    if (!data?.url || !data?.token)
        throw new Error('livekit token response malformed');
    return { url: data.url, token: data.token };
}
